import React from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
}) => {
  const handleRetry = () => {
    resetError();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  const handleReportError = () => {
    if (import.meta.env.MODE === "development") {
      console.log("Error reporting disabled in development mode");
      return;
    }

    Sentry.captureException(error);
    // Show a toast or notification that the error has been reported
    console.log("Error reported to Sentry");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            We're sorry, but something unexpected happened. Our team has been
            notified.
          </p>

          {import.meta.env.MODE === "development" && (
            <details className="mt-4 p-3 bg-muted rounded-md">
              <summary className="cursor-pointer text-sm font-medium">
                Error Details (Development)
              </summary>
              <pre className="mt-2 text-xs text-muted-foreground overflow-auto">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}

          <div className="flex flex-col gap-2">
            <Button onClick={handleRetry} className="w-full">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            <Button variant="secondary" onClick={handleGoHome} className="w-full">
              <Home className="h-4 w-4 mr-2" />
              Go Home
            </Button>
            <Button
              variant="outline"
              onClick={handleReportError}
              className="w-full text-sm"
            >
              Report this error
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const SentryErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  fallback: Fallback = DefaultErrorFallback,
}) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <Fallback error={error as Error} resetError={resetError} />
      )}
      showDialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryErrorBoundary;
